import {
  NoteDataSource,
  NoteDataSourceEnum,
  NoteDataSourceSchema,
  Notebook,
} from '@eigtech/notes-types'
import { useQuerySearch } from '@eigtech/ui-shared-search'
import { useMemo, useState } from 'react'

export type UseEntityNotesOptions = {
  defaultDataSources: NoteDataSourceEnum[] | 'all'
}

export function useEntityNotes(
  notebook: Notebook | undefined | false,
  { defaultDataSources }: UseEntityNotesOptions = {
    defaultDataSources: [
      NoteDataSource.CPAP,
      NoteDataSource.CSR,
      NoteDataSource.Delegate,
      NoteDataSource.EmailReceiving,
      NoteDataSource.TPLAP,
      NoteDataSource.Verisk,
    ],
  }
) {
  const [isAscending, setIsAscending] = useState(false)
  const [visibleNoteDataSources, setVisibleNoteDataSources] = useState<NoteDataSourceEnum[]>(
    defaultDataSources === 'all' ? NoteDataSourceSchema.options : defaultDataSources
  )

  // If notebook is `false` or `undefined` return empty array, otherwise return notes
  // If notes in notebook is undefined, return empty array
  const notebookNotes = useMemo(() => (notebook || {})?.notes ?? [], [notebook])

  const { searchResults, query, setQuery } = useQuerySearch(notebookNotes, {
    fields: ['category', 'createdBy', 'dataSource', 'text', 'title'],
    storeFields: ['id'],
  })

  const notes = useMemo(() => {
    const notes = (query ? searchResults : notebookNotes) ?? []

    const filteredByVisibleDataSources = notes.filter(
      (note) =>
        !note.dataSource || visibleNoteDataSources.includes(note.dataSource as NoteDataSourceEnum)
    )

    const filterOutAssignmentImported = filteredByVisibleDataSources.filter(
      (note) => note.text.toLowerCase() !== 'assignment imported from xa'
    )

    const filterOutBlueprintNotes = filterOutAssignmentImported.filter(
      (note) => note.category !== 'Blueprint'
    )

    return filterOutBlueprintNotes
  }, [notebookNotes, query, searchResults, visibleNoteDataSources])

  return {
    notes,
    searchResults: searchResults,
    query,
    setQuery,
    isAscending,
    setIsAscending,
    visibleNoteDataSources,
    setVisibleNoteDataSources,
  }
}
